import { adminCopy } from "../copy/admin";
import type { ContentReport, ContentReportsPage, ContentReportStatus } from "../types";
import { escapeHtml } from "../utils";

type ReportDeps = {
  updateReportStatus: (id: string, status: ContentReportStatus, adminNote: string) => Promise<void>;
  reload: (page?: number) => Promise<void>;
  onError: (message: string) => void;
};

function statusLabel(status: ContentReportStatus): string {
  return {
    new: adminCopy.reportsNew,
    triaged: adminCopy.reportsTriaged,
    fixed: adminCopy.reportsFixed,
    wontfix: adminCopy.reportsWontfix
  }[status];
}

function reportMarkup(report: ContentReport): string {
  const issueLabel = adminCopy.reportIssueTypeLabels[report.issueType];
  const reviewed = report.reviewedAt
    ? `<p class="muted">${adminCopy.reportReviewedBy} ${escapeHtml(report.reviewedByUsername || adminCopy.unknownReviewer)} · ${escapeHtml(report.reviewedAt)}</p>`
    : "";
  return `<article class="content-report-row" data-report-id="${escapeHtml(report.id)}">
    <div class="content-report-heading">
      <h3>${escapeHtml(report.recordNumber)}</h3>
      <span class="match-badge">${escapeHtml(statusLabel(report.status))}</span>
    </div>
    <p><strong>${escapeHtml(issueLabel)}</strong></p>
    <p class="content-report-note">${report.note ? escapeHtml(report.note) : `<span class="muted">${adminCopy.reportNoNote}</span>`}</p>
    <p class="muted">${adminCopy.reportBy} ${escapeHtml(report.reportedByUsername)} · ${escapeHtml(report.createdAt)}</p>
    ${reviewed}
    <label class="content-report-admin-note">${adminCopy.reportAdminNote}
      <textarea maxlength="1000" placeholder="${adminCopy.reportAdminNotePlaceholder}">${escapeHtml(report.adminNote || "")}</textarea>
    </label>
    <div class="row-actions">
      <button type="button" class="ghost update-report-status" data-status="triaged">${adminCopy.reportMarkTriaged}</button>
      <button type="button" class="ghost update-report-status" data-status="fixed">${adminCopy.reportMarkFixed}</button>
      <button type="button" class="ghost update-report-status" data-status="wontfix">${adminCopy.reportMarkWontfix}</button>
    </div>
  </article>`;
}

export function reportsMarkup(payload: ContentReportsPage): string {
  const pageCopy = adminCopy.reportPage.replace("{page}", String(payload.page)).replace("{total}", String(payload.totalPages));
  return `<section class="content-reports">
    <h2>${adminCopy.reportsHeading}</h2>
    ${payload.reports.length ? payload.reports.map(reportMarkup).join("") : `<p class="muted">${adminCopy.noReports}</p>`}
    <nav class="content-report-pagination" aria-label="${escapeHtml(pageCopy)}">
      <button type="button" class="ghost report-page" data-page="${payload.page - 1}" ${payload.page <= 1 ? "disabled" : ""}>${adminCopy.reportPreviousPage}</button>
      <span>${escapeHtml(pageCopy)}</span>
      <button type="button" class="ghost report-page" data-page="${payload.page + 1}" ${payload.page >= payload.totalPages ? "disabled" : ""}>${adminCopy.reportNextPage}</button>
    </nav>
  </section>`;
}

export function bindReportsSection(deps: ReportDeps): void {
  document.querySelectorAll<HTMLButtonElement>(".update-report-status").forEach((button) => {
    button.addEventListener("click", async () => {
      const row = button.closest<HTMLElement>(".content-report-row");
      if (!row) return;
      button.disabled = true;
      try {
        await deps.updateReportStatus(
          row.dataset.reportId || "",
          button.dataset.status as ContentReportStatus,
          row.querySelector<HTMLTextAreaElement>(".content-report-admin-note textarea")?.value || ""
        );
        await deps.reload();
      } catch (error) {
        button.disabled = false;
        deps.onError(error instanceof Error ? error.message : adminCopy.updateReportFailure);
      }
    });
  });
  document.querySelectorAll<HTMLButtonElement>(".report-page").forEach((button) => {
    button.addEventListener("click", () => void deps.reload(Number(button.dataset.page || 1)));
  });
}
